import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  styled,
} from "@mui/material";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showToast } from "@/redux/actions/toast-actions";
import { useAdminRoles } from "@/hooks/use-admin-role";
import { formatLabel } from "@/utils/format-label";
import { formatRole } from "@/utils/format-role";
import { CustomButton } from "@/components/custom/custom-button";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { useFormik } from "formik";
import { changeRoleSchema } from "@/schemas/user.schema";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(1),
    maxWidth: 440,
    width: "100%",
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "10px",
  backgroundColor: theme.palette.primary.main + "18",
}));

const RoleChip = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: theme.spacing(0.5, 1.5),
  borderRadius: "6px",
  backgroundColor: theme.palette.action.selected,
  fontSize: "0.82rem",
  fontWeight: 500,
}));

const ChangeRoleDialog = ({ open, onClose, admin, mutationFn }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { data: roles } = useAdminRoles();

  const roleList = roles?.data ?? [];

  const formik = useFormik({
    initialValues: {
      roleName: "",
      description: "",
    },
    validationSchema: changeRoleSchema,
    onSubmit: (values) => {
      mutation.mutate({
        userId: admin?.id,
        payload: {
          name: values.roleName,
          description: values.description || "",
        },
      });
    },
  });

  const mutation = useMutation({
    mutationFn: mutationFn,
    onSuccess: (data) => {
      dispatch(
        showToast(data?.message ?? "Role changed successfully", "success"),
      );
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      handleClose();
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    mutation.reset();
    onClose();
  };

  const roleOptions = roleList
    .filter((r) => !admin?.roleType?.includes(r.name))
    .map((r) => ({
      label: r.description || formatLabel(r.name),
      value: r.name,
    }));

  const currentRoleLabel = admin?.roleType?.length
    ? admin.roleType.map(formatRole).join(", ")
    : "N/A";

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pt: 2,
          px: 2,
          pb: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconWrapper>
            <ManageAccountsIcon sx={{ color: "primary.main", fontSize: 22 }} />
          </IconWrapper>
          <Box>
            <DialogTitle sx={{ p: 0, fontSize: "1rem", fontWeight: 600 }}>
              Change Admin Role
            </DialogTitle>
            <Typography variant="caption" color="text.secondary">
              Update role for{" "}
              <strong>
                {admin?.firstName && admin?.surname
                  ? `${admin.firstName} ${admin.surname}`
                  : admin?.email}
              </strong>
            </Typography>
          </Box>
        </Box>

        <Divider />

        <Box>
          <Typography variant="caption" color="text.secondary">
            Current Role
          </Typography>
          <RoleChip>{currentRoleLabel}</RoleChip>
        </Box>

        <Box display="flex" flexDirection="column" gap={2}>
          <FormSelectInput
            formik={formik}
            name="roleName"
            label="New Role"
            options={roleOptions}
            onChange={(e) => {
              const value = e.target.value;
              formik.setFieldValue("roleName", value);

              const selected = roleList.find((r) => r.name === value);
              formik.setFieldValue("description", selected?.description || "");
            }}
          />

          <FormTextInput
            formik={formik}
            name="description"
            label="Description (optional)"
          />
        </Box>

        {mutation.isError && (
          <Typography variant="body2" color="error">
            {mutation.error?.message ?? "Failed to update role"}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 2, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
        >
          Cancel
        </CustomButton>

        <CustomButton
          size="small"
          onClick={formik.handleSubmit}
          disabled={!formik.isValid || !formik.dirty || mutation.isPending}
        >
          {mutation.isPending ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={14} sx={{ color: "#fff" }} />
              Saving...
            </Box>
          ) : (
            "Save Changes"
          )}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default ChangeRoleDialog;
